import ENV from 'env';
import { LatLng } from './spotSearch';

/**
 * Location search for the Find Parking bar, backed by the Mapbox Search Box API.
 * `suggestPlaces` powers typing (venue names such as "Chase Field", addresses, cities);
 * `retrievePlace` resolves a chosen suggestion to coordinates. Both calls in one typing
 * session share a session token, which is how Mapbox bills an autocomplete session.
 */

export interface PlaceSuggestion {
    id: string;
    /** Primary line, e.g. "Chase Field" or "1352 South King Street". */
    name: string;
    /** Secondary line, e.g. "Sports · Phoenix, Arizona 85004, United States". */
    detail: string;
    /** Search Box feature type: poi, address, street, neighborhood, postcode, locality, place, region. */
    placeTypes: string[];
}

export interface ResolvedPlace extends PlaceSuggestion {
    center: LatLng;
}

interface SuggestOptions {
    sessionToken: string;
    proximity?: LatLng;
    signal?: AbortSignal;
    limit?: number;
}

const SEARCH_BOX_URL = 'https://api.mapbox.com/search/searchbox/v1';
const RESULT_TYPES = 'poi,address,street,neighborhood,postcode,locality,place,region';

export function newSessionToken(): string {
    const cryptoApi = typeof crypto !== 'undefined' ? (crypto as any) : undefined;
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

const titleCase = (value: string) => value.replace(/\b\w/g, (c) => c.toUpperCase());

function toSuggestion(raw: any): PlaceSuggestion {
    const category: string | undefined = raw.poi_category && raw.poi_category[0];
    const place: string = raw.place_formatted || raw.full_address || '';
    return {
        id: raw.mapbox_id,
        name: raw.name_preferred || raw.name || place,
        detail: category ? [titleCase(category), place].filter(Boolean).join(' · ') : place,
        placeTypes: raw.feature_type ? [raw.feature_type] : [],
    };
}

/** Autocomplete suggestions for free text, biased toward `proximity` when given. No coordinates yet. */
export async function suggestPlaces(query: string, options: SuggestOptions): Promise<PlaceSuggestion[]> {
    const trimmed = query.trim();
    if (!trimmed) return [];

    const params = new URLSearchParams({
        q: trimmed,
        access_token: ENV.MAPBOX_TOKEN,
        session_token: options.sessionToken,
        limit: String(options.limit ?? 6),
        types: RESULT_TYPES,
        country: 'us,ca',
        language: 'en',
    });
    if (options.proximity) {
        params.set('proximity', `${options.proximity.lng},${options.proximity.lat}`);
    }

    const response = await fetch(`${SEARCH_BOX_URL}/suggest?${params.toString()}`, { signal: options.signal });
    if (!response.ok) {
        throw new Error(`Place search failed with status ${response.status}`);
    }
    const body = await response.json();

    // Mapbox often returns the same venue several times (one per entrance or parking structure).
    const seen = new Set<string>();
    return (body.suggestions || []).map(toSuggestion).filter((suggestion: PlaceSuggestion) => {
        const key = `${suggestion.name}|${suggestion.detail}`.toLowerCase();
        if (!suggestion.id || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

/** Coordinates for a suggestion the user picked. Ends the session `sessionToken` belongs to. */
export async function retrievePlace(suggestion: PlaceSuggestion, sessionToken: string): Promise<ResolvedPlace> {
    const params = new URLSearchParams({ access_token: ENV.MAPBOX_TOKEN, session_token: sessionToken });
    const response = await fetch(
        `${SEARCH_BOX_URL}/retrieve/${encodeURIComponent(suggestion.id)}?${params.toString()}`
    );
    if (!response.ok) {
        throw new Error(`Place lookup failed with status ${response.status}`);
    }
    const body = await response.json();
    const feature = body.features && body.features[0];
    if (!feature || !feature.geometry) {
        throw new Error('Place lookup returned no location');
    }
    const [lng, lat] = feature.geometry.coordinates;
    return { ...suggestion, center: { lng, lat } };
}

/** A sensible map zoom for the kind of place the user picked. */
export function zoomForPlace(placeTypes: string[]): number {
    if (placeTypes.includes('address') || placeTypes.includes('poi')) return 15;
    if (placeTypes.includes('street')) return 14;
    if (placeTypes.includes('neighborhood') || placeTypes.includes('postcode')) return 13;
    if (placeTypes.includes('locality') || placeTypes.includes('place')) return 12;
    if (placeTypes.includes('region')) return 8;
    return 11;
}
